import React from 'react';
import { Upload, Button, Image, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd/es/upload/interface';
// Removed uploadApi import as we now handle files directly in the form

interface ImageUploadProps {
  value?: string | File;
  onChange?: (value: string | File | undefined) => void;
  disabled?: boolean;
  maxSize?: number; // MB
  accept?: string;
  width?: number;
  height?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  disabled = false,
  maxSize = 5, // 5MB default
  accept = 'image/*',
  width = 200,
  height = 150,
}) => {
  // Create preview URL for file or use URL string
  const getPreviewUrl = () => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (value instanceof File) return URL.createObjectURL(value);
    return undefined;
  };

  const previewUrl = getPreviewUrl();

  const beforeUpload = (file: File) => {
    const isValidType = file.type.startsWith('image/');
    if (!isValidType) {
      message.error('Only image files can be uploaded!');
      return false;
    }

    const isValidSize = file.size / 1024 / 1024 < maxSize;
    if (!isValidSize) {
      message.error(`File size must be less than ${maxSize}MB!`);
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!beforeUpload(file)) {
      return;
    }

    onChange?.(file);
    message.success('Image selected!');
  };


  const handleRemove = () => {
    onChange?.(undefined);
  };

  const uploadProps: UploadProps = {
    accept,
    beforeUpload: (file) => {
      handleFileSelect(file);
      return false; // Prevent default upload
    },
    showUploadList: false,
    disabled,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {previewUrl ? (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <Image
            src={previewUrl}
            alt="Selected image"
            style={{
              width,
              height,
              objectFit: 'cover',
              borderRadius: 8,
              border: '1px solid #d9d9d9',
            }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
          {!disabled && (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
              }}
              onClick={handleRemove}
              title="Delete image"
            />
          )}
          
        </div>
      ) : (
        <Upload {...uploadProps}>
          <div
            style={{
              width,
              height,
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
              backgroundColor: disabled ? '#f5f5f5' : '#fafafa',
              transition: 'border-color 0.3s',
            }}
            className="upload-area"
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.borderColor = '#1890ff';
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled) {
                e.currentTarget.style.borderColor = '#d9d9d9';
              }
            }}
          >
            <PlusOutlined style={{ fontSize: 24, color: '#999', marginBottom: 8 }} />
            <div style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>
              Click to select image
              <br />
              <span style={{ fontSize: 10, color: '#999' }}>
                Max {maxSize}MB
              </span>
            </div>
          </div>
        </Upload>
      )}

      
    </div>
  );
};

export default ImageUpload;